<template>
  <v-container fluid>
    <!-- Point Types -->
    <v-row class="mt-2">
      <v-col cols="12">
        <h2>Point Types</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="4" v-for="pointType in pointTypes" :key="pointType.id">
        <v-card outlined class="pa-3">
          <div class="text-subtitle-1">{{ pointType.name }}</div>
          <div class="text-caption grey--text">Bank: {{ pointType.bank }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" v-if="!pointTypesLoading && pointTypes.length === 0">
        <span class="grey--text">No point types yet. Add one below.</span>
      </v-col>
    </v-row>
    <v-row class="mt-2 align-center d-flex" align="stretch">
      <v-col cols="12" sm="6" md="4">
        <core-text-field
          v-model="newPointTypeName"
          label="New Point Type Name"
          dense
          hide-details="auto"></core-text-field>
      </v-col>
      <v-col cols="12" sm="auto">
        <core-btn-primary
          :block="$vuetify.breakpoint.xsOnly"
          :loading="pointTypeSaving"
          :disabled="!newPointTypeName"
          @click="addPointType()">
          Add Point Type<v-icon right>mdi-plus</v-icon>
        </core-btn-primary>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <!-- Habits -->
    <v-row>
      <v-col cols="12">
        <h2>Habits</h2>
      </v-col>
    </v-row>
    <v-data-table
      :headers="habitHeaders"
      :items="habits"
      :loading="habitsLoading"
      hide-default-footer
      class="elevation-1">
      <template v-slot:[`item.point_type`]="{ item }">
        {{ pointTypeName(item.point_type) }}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex flex-wrap justify-end align-center py-1">
          <v-btn
            small
            text
            color="secondary"
            :loading="generatingId === item.id"
            :aria-label="`Generate remaining days for ${item.name}`"
            @click="generateDays(item)">
            <span class="d-none d-sm-inline">Generate Tracking Days</span>
            <v-icon right small class="ml-1">mdi-calendar-plus</v-icon>
          </v-btn>
          <v-btn
            small
            icon
            color="bad"
            :aria-label="`Delete ${item.name}`"
            @click="confirmDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-row class="mt-4 align-center" align="stretch">
      <v-col cols="12" sm="6" md="3">
        <core-text-field
          v-model="newHabit.name"
          label="New Habit Name"
          dense
          hide-details="auto"></core-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <core-select
          v-model="newHabit.point_type"
          :items="pointTypes"
          item-text="name"
          item-value="id"
          label="Point Type"
          dense
          hide-details="auto">
        </core-select>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <div class="d-flex align-center">
          <core-number-field
            v-model="newHabit.point_reward"
            label="Point Reward"
            dense
            hide-details="auto"
            class="flex-grow-1"></core-number-field>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" color="grey" class="ml-1"
                >mdi-information-outline</v-icon
              >
            </template>
            <span>
              Completing a habit adds its Point Reward to its Point Type's bank.<br />
              Complete a habit every day Sun-Sat and you'll earn a 10x bonus on Saturday.<br />
              Complete a habit every day of the month and you'll earn a 40x bonus on the last
              day.<br />
            </span>
          </v-tooltip>
        </div>
      </v-col>
      <v-col cols="12" sm="6" md="auto">
        <core-btn-primary
          :block="$vuetify.breakpoint.xsOnly"
          :loading="habitSaving"
          :disabled="!canAddHabit"
          @click="addHabit()">
          Add Habit<v-icon right>mdi-plus</v-icon>
        </core-btn-primary>
      </v-col>
    </v-row>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Delete Habit</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ habitToDelete && habitToDelete.name }}"? This will
          also remove all of its tracked days.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <core-btn-neutral @click="deleteDialog = false">Cancel</core-btn-neutral>
          <core-btn-negative :loading="deleting" @click="deleteHabit()">Delete</core-btn-negative>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      pointTypes: [],
      pointTypesLoading: true,
      newPointTypeName: "",
      pointTypeSaving: false,

      habits: [],
      habitsLoading: true,
      newHabit: { name: "", point_type: null, point_reward: 1 },
      habitSaving: false,
      generatingId: null,

      deleteDialog: false,
      habitToDelete: null,
      deleting: false,

      habitHeaders: [
        { text: "Name", value: "name" },
        { text: "Point Type", value: "point_type" },
        { text: "Point Reward", value: "point_reward" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    }
  },
  computed: {
    canAddHabit() {
      return !!this.newHabit.name && !!this.newHabit.point_type
    },
  },
  methods: {
    pointTypeName(id) {
      let pointType = this.pointTypes.find((p) => p.id === id)
      return pointType ? pointType.name : ""
    },
    async loadPointTypes() {
      this.pointTypesLoading = true
      await this.$django_api.get("habits/point_type/").then((response) => {
        this.pointTypes = response.data
      })
      this.pointTypesLoading = false
    },
    async loadHabits() {
      this.habitsLoading = true
      await this.$django_api.get("habits/habit/").then((response) => {
        this.habits = response.data
      })
      this.habitsLoading = false
    },
    async addPointType() {
      this.pointTypeSaving = true
      await this.$django_api
        .post("habits/point_type/", { name: this.newPointTypeName })
        .then((response) => {
          this.pointTypes.push(response.data)
          this.newPointTypeName = ""
        })
      this.pointTypeSaving = false
    },
    async addHabit() {
      this.habitSaving = true
      await this.$django_api.post("habits/habit/", this.newHabit).then((response) => {
        this.habits.push(response.data)
        this.newHabit = { name: "", point_type: null, point_reward: 1 }
      })
      this.habitSaving = false
    },
    async generateDays(habit) {
      this.generatingId = habit.id
      await this.$django_api
        .post("habits/habit/generate_days/", { habit_id: habit.id })
        .then(() => {
          let alert = {
            type: "success",
            message: `Generated tracking days for the rest of the year for "${habit.name}".`,
          }
          this.$store.dispatch("set_alert", alert)
        })
      this.generatingId = null
    },
    confirmDelete(habit) {
      this.habitToDelete = habit
      this.deleteDialog = true
    },
    async deleteHabit() {
      this.deleting = true
      await this.$django_api.delete(`habits/habit/?id=${this.habitToDelete.id}`).then(() => {
        this.habits = this.habits.filter((h) => h.id !== this.habitToDelete.id)
      })
      this.deleting = false
      this.deleteDialog = false
    },
  },
  mounted: async function () {
    await this.loadPointTypes()
    await this.loadHabits()
  },
}
</script>
